import React, { useState, useEffect } from "react";
import { InfoCircle } from "iconsax-react";
import { useForm } from "react-hook-form";
import { useUploadFile } from "../../../components/upload_file_card";
import { AZURE_BLOB_CONTAINER_CERTIFICATES } from "../../../../../constants";
import beveatApiResourceOwner from "../../../../api/resource_owner_beveat_api.js";
import { commonConfigs, urls } from "../../../../../config";

const errorIcon = (
  <div className="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none">
    <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
  </div>
);

const UpdateCertificateTemplateModal = ({
  isOpen,
  onClose,
  onUpdateSuccess,
  templateData,
}) => {
  const [file, fileCard, uploadFile, resetFile] = useUploadFile({ 
    container: AZURE_BLOB_CONTAINER_CERTIFICATES,
    acceptedFileTypes: "image",
    displayText: "PNG, JPG files only"
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    register,
    setError,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      courseCode: "",
    }
  });

  useEffect(() => {
    if (templateData && isOpen) {
      setValue("courseCode", templateData.courseCode || "");
    }
  }, [templateData, isOpen, setValue]);

  async function updateCertificateTemplate(certificateId, updateData) {
    const updateTemplateUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.updateCertificateTemplate(certificateId);
    
    await beveatApiResourceOwner.put(updateTemplateUrl, updateData, {
      headers: commonConfigs.resourceOwnerApiHeader,
    })
    .then((res) => {
      console.log("Template updated successfully:", res.data);
      return res.data;
    })
    .catch((err) => {
      console.error("Error updating template:", err);
      throw err;
    });
  }

  const onSubmit = async (data) => {
    setIsUpdating(true);

    try {
      let certificateUrl = templateData.templateUrl;
      if (file) {
        console.log('Uploading new template file...');
        
        const originalData = templateData._original;
        const proficiencyFolder = originalData?.certificate_proficiency_syscode === 1 ? "coc" : "coa";
        const blobPath = `templates/${proficiencyFolder}/${file.name}`;
        
        console.log('Template update path:', blobPath);
        
        certificateUrl = await uploadFile(blobPath);
        console.log('New template uploaded:', certificateUrl);
      }
      
      const updateData = {
        certificate_img_url: certificateUrl,
        course_code: data.courseCode,
      };

      console.log('Template data to update:', updateData);

      const certificateId = templateData._original?.certificate_template_id || templateData._original?.certificate_id;
      
      if (!certificateId) {
        throw new Error("Certificate ID not found in template data");
      }

      await updateCertificateTemplate(certificateId, updateData);

      alert("Template updated successfully");
      handleClose();
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
    } catch (error) {
        const errorMessage = error || "An error occurred during upload";
        alert(`Error: ${errorMessage}`);  
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    reset();
    resetFile();
    onClose();
  };

  if (!isOpen || !templateData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-3 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Update Template</h2>
        </div>
        
        <div className="px-6 py-2">
          {/* Template Details Section */}
          <div className="mb-3 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Template Details</h3>
            <p className="text-sm text-gray-700"><strong>Coursework:</strong> {templateData.coursework}</p>
            <p className="text-sm text-gray-700"><strong>Environment:</strong> {templateData.environment}</p>
            <p className="text-sm text-gray-700"><strong>Proficiency:</strong> {templateData.proficiency}</p>
            <p className="text-sm text-gray-700"><strong>Training Provider:</strong> {templateData.trainingProvider}</p>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Course Code */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Code
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter Course Code (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm"
                  {...register("courseCode")}
                />
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Certificate Template File (PNG, JPG)
              </label>
              {fileCard}
            </div>
          </form>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isUpdating}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={isUpdating}
            className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 transition-colors duration-200 ${
              !isUpdating
                ? "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateCertificateTemplateModal;